import type { ResourceTableDescriptor, TranslateFn } from '@/components/ui/embedded-resource-table/types';
import type { ToolbarFilterOption } from '@/components/ui/generic-toolbar/GenericToolbar';
import { assignableUserId, buildAssignedUserOptions, type AssignableUser } from '@/lib/assigned-user-options';
import { settingLabel } from '@/lib/status-labels';
import type { Company } from '@/types';

/** The categories a company carries, whether stored as a list or a single value. */
function categories(company: Company): string[] {
    const raw = company.category;

    if (Array.isArray(raw)) {
        return raw.map((value) => String(value));
    }

    return raw ? [String(raw)] : [];
}

function assignedUserIds(company: Company): string[] {
    return (company.users ?? []).map((user) => user.id).filter(Boolean);
}

/**
 * The one description of the companies table, read by `pages/companies/index.tsx`,
 * by every detail-page tab that lists companies, and by CompanyTable itself.
 *
 * Deliberately absent: vat, address*, phone, email. They are GDPR-sensitive fields
 * decrypted at the middleware layer, and belong on the detail page rather than a
 * list view.
 *
 * `sort.serverKey` must exist in CompanyController@index's $dbSortColumns — the
 * controller silently falls back to ordering by uuid for anything else, so an
 * unsortable column would appear to work while quietly reshuffling the table.
 */
export const companyTableDescriptor: ResourceTableDescriptor<Company> = {
    key: 'companies',
    resourceName: 'companies',
    resourceNamePlural: 'companies',
    getRowId: (company) => company.id,

    columns: [
        {
            id: 'name',
            labelKey: 'companies.index.columns.name',
            labelFallback: 'Name',
            sort: { serverKey: 'name', value: (company) => company.name ?? '' },
        },
        {
            id: 'company_code',
            labelKey: 'companies.index.columns.code',
            labelFallback: 'Code',
            sort: { serverKey: 'company_code', value: (company) => company.company_code ?? '' },
        },
        {
            id: 'type',
            labelKey: 'companies.index.columns.type',
            labelFallback: 'Type',
            sort: { serverKey: 'type', value: (company) => String(company.type ?? '') },
        },
        {
            id: 'priority',
            labelKey: 'companies.index.columns.priority',
            labelFallback: 'Priority',
            sort: { serverKey: 'priority', value: (company) => String(company.priority ?? '') },
        },
        { id: 'category', labelKey: 'companies.index.columns.category', labelFallback: 'Category' },
        {
            id: 'domain',
            labelKey: 'companies.index.columns.domain',
            labelFallback: 'Domain',
            sort: { serverKey: 'domain', value: (company) => company.domain ?? '' },
        },
        { id: 'country', labelKey: 'companies.index.columns.country', labelFallback: 'Country' },
        { id: 'revenue', labelKey: 'companies.index.columns.revenue', labelFallback: 'Revenue' },
        { id: 'employees', labelKey: 'companies.index.columns.employees', labelFallback: 'Employees' },
        {
            id: 'total_profit',
            labelKey: 'companies.index.columns.profit',
            labelFallback: 'Profit',
            sort: { serverKey: 'total_profit', value: (company) => Number(company.total_profit ?? 0) },
        },
        {
            id: 'total_people',
            labelKey: 'companies.index.columns.contacts',
            labelFallback: 'Contacts',
            sort: { serverKey: 'total_people', value: (company) => Number(company.total_people ?? 0) },
        },
        {
            id: 'users',
            labelKey: 'companies.index.columns.assigned_to',
            labelFallback: 'Assigned To',
            sort: { serverKey: 'users', value: (company) => (company.users ?? []).length },
        },
        {
            id: 'created_at',
            labelKey: 'companies.index.columns.created',
            labelFallback: 'Created',
            sort: { serverKey: 'created_at', value: (company) => company.created_at ?? '' },
        },
    ],

    /** Category replaces domain here: it classifies the business, domain is just a URL. */
    defaultVisibleColumns: ['name', 'company_code', 'type', 'priority', 'category', 'total_profit', 'total_people', 'users'],

    filters: [
        {
            id: 'type',
            param: 'type_filter',
            labelKey: 'companies.index.quick_filters.type',
            labelFallback: 'Type',
            matches: (company, values) => values.includes(String(company.type ?? '')),
        },
        {
            id: 'priority',
            param: 'priority_filter',
            labelKey: 'companies.index.quick_filters.priority',
            labelFallback: 'Priority',
            matches: (company, values) => values.includes(String(company.priority ?? '')),
        },
        {
            id: 'category',
            param: 'category_filter',
            labelKey: 'companies.index.quick_filters.category',
            labelFallback: 'Category',
            // Any match, mirroring the controller's orWhereJsonContains.
            matches: (company, values) => categories(company).some((category) => values.includes(category)),
        },
        {
            id: 'users',
            param: 'assigned_user_id',
            labelKey: 'companies.index.columns.assigned_to',
            labelFallback: 'Assigned To',
            matches: (company, values) => assignedUserIds(company).some((id) => values.includes(id)),
        },
    ],

    quickFilterIds: ['type', 'priority', 'category', 'users'],

    // Name, code and domain are unique per company, so a checkbox list of them
    // would be useless — the search box covers them instead.
    searchValues: (company) => [company.name, company.company_code, company.domain, company.country],

    allLabel: { labelKey: 'companies.index.filters.all_companies', labelFallback: 'All Companies' },
    quickFiltersLabel: { labelKey: 'companies.index.toolbar.quick_filters', labelFallback: 'Quick filters' },
};

export interface CompanyFilterOptionSources {
    t: TranslateFn;
    types: string[];
    priorities: Array<{ value: string }>;
    categories: string[];
    users: AssignableUser[];
}

export function companyFilterOptions({
    t,
    types,
    priorities,
    categories: categoryValues,
    users,
}: CompanyFilterOptionSources): Record<string, ToolbarFilterOption[]> {
    return {
        // Labels resolve through settingLabel so options follow the UI language
        // rather than showing the raw stored value.
        type: types.map((type) => ({ value: type, label: settingLabel(t, 'company_types', type) })),
        priority: priorities.map((priority) => ({ value: priority.value, label: settingLabel(t, 'company_priorities', priority.value) })),
        category: categoryValues.map((category) => ({ value: category, label: settingLabel(t, 'company_categories', category) })),
        users: buildAssignedUserOptions(users, t),
    };
}

/** Fallback sources for a tab handed rows but no reference lists. */
export function companyOptionSourcesFromRows(companies: Company[]): Pick<CompanyFilterOptionSources, 'types' | 'categories' | 'users'> {
    const types = new Set<string>();
    const categoryValues = new Set<string>();
    const users = new Map<string, AssignableUser>();

    companies.forEach((company) => {
        if (company.type) {
            types.add(String(company.type));
        }
        categories(company).forEach((category) => categoryValues.add(category));
        (company.users ?? []).forEach((user) => {
            const id = assignableUserId(user as AssignableUser);
            if (id) {
                users.set(id, user as AssignableUser);
            }
        });
    });

    return { types: [...types], categories: [...categoryValues], users: [...users.values()] };
}
